import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { VisuallyHidden } from "@/components/ui/visually-hidden";
import LeadForm from "@/components/LeadForm";

const KEY = "pcv_lead_popup_shown_at";
const COOLDOWN_MS = 24 * 60 * 60 * 1000; // 24h

// Routes where the popup must not appear — the Purva City Spire page carries
// only RERA-verified facts and cannot show pricing creatives from another phase.
const SUPPRESSED_PATHS = ["/purva-city-spire"];

export default function LeadPopup() {
  const { pathname } = useLocation();
  const suppressed = SUPPRESSED_PATHS.some((p) => pathname.replace(/\/+$/, "") === p);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (suppressed) return;
    const seen = Number(localStorage.getItem(KEY) || 0);
    if (Date.now() - seen < COOLDOWN_MS) return;

    let shown = false;
    const show = () => {
      if (shown) return;
      shown = true;
      setOpen(true);
      localStorage.setItem(KEY, String(Date.now()));
    };

    const t = setTimeout(show, 15000);

    const onScroll = () => {
      const scrollPct = (window.scrollY + window.innerHeight) / document.body.scrollHeight;
      if (scrollPct > 0.5) show();
    };
    const onLeave = (e) => {
      if (e.clientY <= 0) show();
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    document.addEventListener("mouseleave", onLeave);

    return () => {
      clearTimeout(t);
      window.removeEventListener("scroll", onScroll);
      document.removeEventListener("mouseleave", onLeave);
    };
  }, [suppressed]);

  if (suppressed) return null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        data-testid="lead-popup"
        className="glass-strong border border-[color:var(--pcv-border)] max-w-[440px] p-0 overflow-hidden text-[color:var(--pcv-text)]"
      >
        <VisuallyHidden>
          <DialogTitle>Unlock Launch-Phase Pricing at Provident Winworth Kochi</DialogTitle>
          <DialogDescription>Request the confidential cost sheet and floor plans.</DialogDescription>
        </VisuallyHidden>
        <div className="grid">
          <div
            className="h-32 relative"
            style={{
              backgroundImage: "url(https://customer-assets.emergentagent.com/job_purva-flagship-kochi/artifacts/cr99qck2_Winworth_1920px-x-600px.jpg)",
              backgroundSize: "cover", backgroundPosition: "center",
            }}
          >
            <div className="absolute inset-0 bg-gradient-to-b from-black/20 to-[color:var(--pcv-bg)]" />
          </div>
          <div className="p-6 -mt-10 relative">
            <div className="eyebrow">Exclusive VIP Access</div>
            <h3 className="font-serif-lux text-3xl mt-2 leading-none">Unlock <span className="text-[color:var(--pcv-gold)]">Launch-Phase Pricing</span></h3>
            <p className="text-sm text-[color:var(--pcv-text-2)] mt-2 leading-relaxed">
              Get the confidential cost sheet, floor plans and pre-launch investor pricing before public release.
            </p>
            <div className="mt-4">
              <LeadForm source="popup" showNRIFields ctaLabel="Send Me the Cost Sheet" onSuccess={() => setTimeout(() => setOpen(false), 900)} />
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
